import React, { useCallback, useEffect, useState } from 'react';
import cn from 'clsx';

import Button from '../../library/button/button';
import Collapsible from '../../library/collapsible/collapsible';
import Message from '../message/message';

import { useFitment, useMessage, usePrevious } from '../../shared/hooks/hooks';

import { trackAction } from '../../shared/utils/analytics/analytics';
import { getNormalizedProductType } from '../../shared/utils/product/product';

import {
  AWD_BELOW_RECOMMENDED_QUANTITY_LONG_MSG,
  AWD_BELOW_RECOMMENDED_QUANTITY_SHORT_MSG
} from '../../shared/constants/messages';
import { PRODUCT_TYPES } from '../../shared/constants/products';

import styles from './quantity-disclaimer.scss';

interface QuantityDisclaimerProps {
  className?: string;
  messageContext?: string;
  productType: string | null | undefined;
  quantity: number | undefined;
  showBtnBottom?: boolean;
}

function QuantityDisclaimer(props: QuantityDisclaimerProps) {
  const {
    className,
    messageContext,
    productType,
    quantity,
    showBtnBottom = false
  } = props;

  const [isExpanded, setIsExpanded] = useState(false);
  const { selectedFitment } = useFitment();
  const { isAWD, isStaggered } = selectedFitment ?? {};

  const messages = useMessage(
    [
      AWD_BELOW_RECOMMENDED_QUANTITY_LONG_MSG,
      AWD_BELOW_RECOMMENDED_QUANTITY_SHORT_MSG
    ],
    !isAWD || getNormalizedProductType(productType) !== PRODUCT_TYPES.TIRES
  );

  const shouldDisplayAWDQuantityDisclaimer =
    isAWD &&
    messages &&
    typeof quantity === 'number' &&
    getNormalizedProductType(productType) === PRODUCT_TYPES.TIRES &&
    ((isStaggered && quantity < 2) || (!isStaggered && quantity < 4));

  const trackAWDAction = useCallback(
    (identifierSuffix?) => {
      trackAction(
        `fitment_message_awd_qty_sub_reco${
          identifierSuffix ? `_${identifierSuffix}` : ''
        }`,
        {
          message_context: messageContext ?? null,
          product_quantity: quantity
        }
      );
    },
    [quantity, messageContext]
  );

  const previousShouldDisplayAWDQuantityDisclaimer = usePrevious(
    shouldDisplayAWDQuantityDisclaimer
  );

  useEffect(() => {
    if (
      shouldDisplayAWDQuantityDisclaimer &&
      shouldDisplayAWDQuantityDisclaimer !==
        previousShouldDisplayAWDQuantityDisclaimer
    ) {
      trackAWDAction();
    }
  }, [
    previousShouldDisplayAWDQuantityDisclaimer,
    shouldDisplayAWDQuantityDisclaimer,
    trackAWDAction
  ]);

  function handleReadMore() {
    if (!isExpanded) {
      trackAWDAction('read_more');
    }
    setIsExpanded(!isExpanded);
  }

  const readMoreText = `read ${isExpanded ? 'less' : 'more'}`;

  return shouldDisplayAWDQuantityDisclaimer ? (
    <div
      className={cn(
        styles['wrapper'],
        `${className || ''} quantity-disclaimer`
      )}
    >
      <div
        className={cn(styles['container'], 'quantity-disclaimer__container')}
      >
        <Message
          className={styles['quantity-disclaimer-message']}
          isWarning
          message={
            <div>
              {messages[AWD_BELOW_RECOMMENDED_QUANTITY_SHORT_MSG]}{' '}
              {!showBtnBottom && (
                <Button onClick={handleReadMore}>{readMoreText}</Button>
              )}
              <Collapsible
                collapseType="SECONDARY"
                hideToggle
                isOpen={isExpanded}
              >
                {messages.AWD_BELOW_RECOMMENDED_QUANTITY_LONG_MSG}
              </Collapsible>
              {showBtnBottom && (
                <Button onClick={handleReadMore}>{readMoreText}</Button>
              )}
            </div>
          }
        />
      </div>
    </div>
  ) : null;
}

export default QuantityDisclaimer;
